import React, { ChangeEvent, useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

import "./Viewer.css";
import Video from "../../formats/Video/Video";
import Icon from "../../../ui/Icon/Icon";
import "../../../styles/animations/shake.css";
import { Guide, Organization } from "@gigauser/common/src/core/types/guide";

import downArrowheadIcon from "@gigauser/common/src/assets/svgs/downArrowheadIcon";
import { TextEditor } from "../../formats/RichText/TextEditor";

import closeIcon from "../../../assets/svgs/closeIcon";
import { Device } from "../../../types/devices";
import useWindowSize from "../../../hooks/useWindowSize";
import "./Viewer.css";
import { getBrandingColor } from "../../../core/utils/styleUtils";

import { useSearchParams } from "react-router-dom";
import {
	HelpCenterProps,
	Navigate,
} from "../../../help-center/HelpCenter/HelpCenter";
import { Size } from "../../../types/sizes";
import { HelpCenterConfig, Id } from "@giga-user-fern/api/types/api";
import { captureEvent } from "../../../core/analytics/analytics";
import logoIcon from "../../../assets/svgs/logoIcon";
import orgSetupData from "../../../setup/orgSetupData";
import { captureEventInPanchayat } from "../../../utils/panchayat";
import useScrollTracker from "../../../hooks/useScrollTracker";

export type HelpCenterEnvironment = "remote-script" | "ext-hc";

type ViewerProps = {
	guide?: Guide; //If guide is not passed, then loading viewer will be shown.
	onClose: () => void;
	onBack?: () => void;
	overrideDevice?: Device;
	navigator?: () => Navigate;
	size?: Size;
	appTheme?: "gigauser-light" | "gigauser-dark";
	contentOnly?: boolean;
	environment: HelpCenterEnvironment;

	onOpenGuideFromId: (x: Id) => void;
	getOrganization: () => Organization | null;

	showCreator?: boolean;
	helpCenterConfig?: HelpCenterConfig;

	helpCenterProps: HelpCenterProps;
};

const min = 1 / 3;
const mid = 2 / 3;
const max = 3 / 3;

enum CsatFeedback {
	min = 0,
	mid = 1,
	max = 2,
}

export const Viewer: React.FC<ViewerProps> = (props: ViewerProps) => {
	const viewerContainerRef = useRef<HTMLDivElement>(null);
	let guide: Guide | undefined;
	if (props.guide) guide = props.guide;

	let device = useWindowSize().device;
	if (props.overrideDevice) device = props.overrideDevice;
	const gigDevice = "gigauser-" + device;

	const [csatFeedback, setCsatFeedback] = useState<CsatFeedback>();
	const [messageFeedbackDone, setMessageFeedbackDone] = useState(false);
	const [messageFeedback, setMessageFeedback] = useState("");

	const loading = !props.guide;

	useEffect(() => {
		setCsatFeedback(undefined);
		setMessageFeedbackDone(false);
		setMessageFeedback("");

		if (gigDevice === "gigauser-mobile" && props.guide) {
			captureEventInPanchayat({
				eventName: "GuideOpenedInHelpCenter",
				value: {
					guide_id: guide?.id.toString() as string,
					organization_id: props.helpCenterProps
						.getOrganization()
						?.id.toString() as string,
					source: "direct",
				},
			});
		}
	}, [props.guide]);

	const submitCsatFeedback = (feedback: CsatFeedback) => {
		setCsatFeedback(feedback);
		captureEventInPanchayat({
			eventName: "GuideRatedInHelpCenter",
			value: {
				guide_id: guide?.id.toString() as string,
				rating: feedback,
				organization_id: props
					.getOrganization()
					?.id.toString() as string,
				session_id: props.helpCenterProps.helpCenterSessionId as string,
			},
		});
	};

	const submitMessageFeedback = () => {
		setMessageFeedbackDone(true);
		captureEventInPanchayat({
			eventName: "GuideFeedbackGivenInHelpCenter",
			value: {
				guide_id: guide?.id.toString() as string,
				feedback: messageFeedback,
				organization_id: props
					.getOrganization()
					?.id.toString() as string,
				session_id: props.helpCenterProps.helpCenterSessionId as string,
			},
		});
	};

	const onChangeMessageFeedback = (e: ChangeEvent<HTMLTextAreaElement>) => {
		setMessageFeedback(e.target.value);
	};

	const closeHelpCenter = () => {
		props.onClose();
	};

	const brandColor = getBrandingColor();

	let videoSrc = props.guide?.guideData.video.publishedVideoSrc || "";

	if (props.guide?.guideData.settings.publishedVideo !== "ai") {
		videoSrc = props.guide?.guideData.video.originalSrc || "";
	}

	const sendAnalytics = (percentage: number, guideId: string) => {
		const milestones = [10, 25, 33, 50, 66, 75, 100];

		if (milestones.includes(percentage) && guideId) {
			captureEventInPanchayat({
				eventName: "GuideScrolledInHelpCenter",
				value: {
					guide_id: guideId,
					organization_id: props.helpCenterProps
						.getOrganization()
						?.id.toString() as string,

					session_id: props.helpCenterProps
						.helpCenterSessionId as string,
					scroll_percentage: percentage,
				},
			});
		}
	};

	// Use the scroll tracking hook
	useScrollTracker(
		viewerContainerRef,
		sendAnalytics,
		guide?.id.toString() as string,
	);

	// if(!videoSrc && isVideoVisible) videoSrc = props.guide?.guideData.video.originalSrc || ""

	const getFormatFromActiveTab = () => {
		if (!props.guide) return <h1>Couldn't get guide!</h1>;

		const articleComponent = (
			<div
				className={`gigauser-guideviewer-text-container ${device === "laptop" ? "gigauser-text-laptop" : "gigauser-text-mobile"}`}
			>
				<TextEditor
					onOpenGuideFromId={props.onOpenGuideFromId}
					type={"helpCenter"}
					size={props.size}
					initialContent={props.guide.guideData.plainDoc.data as any}
					editable={false}
					appTheme={props.appTheme}
				/>

				{window.location.href.includes("share/") ? (
					<a
						className="gigauser-viewer-branding"
						href={
							"https://www.clueso.io/?utm_source=customerKB&utm_medium=poweredby&utm_campaign=" +
							window.location.hostname
						}
						target="_blank"
						rel="noreferrer"
					>
						<p>Created on</p>
						<div className="gigauser-viewer-logo">{logoIcon()}</div>
					</a>
				) : null}
			</div>
		);
		const videoComponent = (
			<Video
				disableDownload={disableDownload}
				hideVideoBranding={hideVideoBranding}
				guidePreview={props.guide.guidePreview}
				video={props.guide.guideData.video}
				videoSrc={videoSrc}
				overrideDevice={props.overrideDevice}
				helpCenterProps={props.helpCenterProps}
			/>
		);

		// if(props.size==="l"){
		return (
			<>
				{videoSrc &&
				props.guide.guideData.settings.publishedVideo !== "none" ? (
					<div className="Viewer-l-video">{videoComponent}</div>
				) : null}

				{props.guide.guideData.settings.hideArticle
					? null
					: articleComponent}
			</>
		);
		// }
	};

	let hideFeedback = false;
	let hideVideoBranding = false;
	let disableDownload = false;

	let feedbackText = "Did you find this article helpful?";
	let feedbackEnterFeedbackTitle = "Thank you! How can we help you better?";
	let feedbackEnterFeedbackPlaceholder = "Enter your feedback here ... ";
	let feedbackSubmitText = "Submit";
	let feedbackResultText = "Thank you for submitting feedback!";
	let createdByText = "Created by ";
	let descriptionColorFlip = false;

	const orgId = props.getOrganization()?.id;
	if (orgId) {
		hideFeedback = !props.helpCenterConfig?.feedbackFlag || false;
		hideVideoBranding = !props.helpCenterConfig?.videoBrandingFlag || false;
		disableDownload = props.helpCenterConfig?.disableDownload || false;

		feedbackText = props.helpCenterConfig?.feedbackPrompt || feedbackText;
		feedbackEnterFeedbackPlaceholder =
			props.helpCenterConfig?.feedbackPlaceholder ||
			feedbackEnterFeedbackPlaceholder;
		feedbackEnterFeedbackTitle =
			props.helpCenterConfig?.feedbackQuestion ||
			feedbackEnterFeedbackTitle;
		feedbackSubmitText =
			props.helpCenterConfig?.feedbackSubmitText || feedbackSubmitText;
		feedbackResultText =
			props.helpCenterConfig?.feedbackRevertText || feedbackResultText;
		createdByText =
			props.helpCenterConfig?.createdByKeyword || createdByText;

		if (orgSetupData[orgId])
			descriptionColorFlip =
				orgSetupData[orgId].descriptionColorInvert ?? false;
	}

	const parts =
		props.guide?.guidePreview.header.description?.split(/(#[\w]+)/);

	return (
		<div className={`gigauser-guide-viewer-container ${gigDevice}`}>
			{props.onBack ? (
				<div className={`gigauser-viewer-header ${gigDevice}`}>
					<div
						className={`gigauser-viewer-back ${gigDevice}`}
						onClick={props.onBack}
					>
						<Icon
							className={`gigauser-viewer-back-button ${gigDevice}`}
						>
							{downArrowheadIcon(
								device === "laptop" ? brandColor : "white",
							)}
						</Icon>

						{device === "laptop" ? (
							<span className="gigauser-back-text">Back</span>
						) : null}

						{device === "mobile" && props.guide ? (
							<div className="gigauser-header-title">
								{props.guide.guidePreview.header.name}{" "}
							</div>
						) : null}
					</div>

					<Icon
						className="gigauser-viewer-close-helpcenter"
						onClick={closeHelpCenter}
						style={{
							visibility:
								device === "laptop" ? "hidden" : "visible",
						}}
					>
						{closeIcon(device === "laptop" ? brandColor : "white")}
					</Icon>
				</div>
			) : null}

			<div
				className={`gigauser-viewer-body ${props.contentOnly ? "gigauser-content-only" : ""} style-scroll-square ${gigDevice}`}
				ref={viewerContainerRef}
			>
				{props.contentOnly ? null : (
					<div
						className={`gigauser-viewer-title ${loading ? "gigauser-loading-title" : "gigauser-not-loading"}`}
					>
						<div
							className={`gigauser-viewer-title-text gigauser-name ${props.size}`}
						>
							{props.guide
								? props.guide.guidePreview.header.name
								: "Random guide name here"}
						</div>

						<div
							className={`gigauser-viewer-title-text 
                            ${loading ? "" : "gigauser-description"}
							${props.appTheme}
                            ${descriptionColorFlip ? "color-flip" : ""}
                        ${props.size}`}
						>
							{parts?.map((part, index) =>
								part.startsWith("#") ? (
									<span
										key={index.toString()}
										className={`CollectionViewer-description-tag ${descriptionColorFlip ? "color-flip" : ""}`}
									>
										{part}
									</span>
								) : (
									<span
										key={index.toString()}
										className={`CollectionViewer-description-non-tag ${descriptionColorFlip ? "color-flip" : ""}`}
									>
										{part}
									</span>
								),
							)}
						</div>

						{props.guide?.guidePreview.author &&
							props.showCreator && (
								<div className="Viewer-creator">
									{createdByText}
									<a
										href={`mailto: ${props.guide.guidePreview.author.email}`}
										className="Viewer-creator-email"
									>
										{props.guide?.guidePreview.author.email}
									</a>
									{/* {props.guide?.guideData.} */}
								</div>
							)}
					</div>
				)}

				<div
					className="gigauser-viewer-tabs"
					style={{
						visibility: props.guide ? "visible" : "hidden",
					}}
				></div>

				{props.guide ? (
					<div className="gigauser-guide-container style-scroll">
						{getFormatFromActiveTab()}

						{device === "laptop" && !hideFeedback ? (
							<div
								className={`gigauser-guide-feedback-container  ${device === "laptop" ? "gigauser-text-laptop" : "gigauser-text-mobile"}`}
							>
								<div className="gigauser-guide-feedback">
									{messageFeedbackDone ? (
										<div>{feedbackResultText}</div>
									) : (
										<>
											<div className="gigauser-feedback-1">
												<div>{feedbackText}</div>

												<div className="gigauser-emojis-container">
													<div
														className={`gigauser-feedback-emoji ${csatFeedback === min ? "gigauser-active-emoji" : csatFeedback ? "gigauser-inactive-emoji" : "gigauser-regular-emoji"}`}
														onClick={
															csatFeedback
																? undefined
																: () => {
																		submitCsatFeedback(
																			min,
																		);
																	}
														}
													>
														{" "}
														<span>😞</span>
													</div>
													<div
														className={`gigauser-feedback-emoji ${csatFeedback === mid ? "gigauser-active-emoji" : csatFeedback ? "gigauser-inactive-emoji" : "gigauser-regular-emoji"}`}
														onClick={
															csatFeedback
																? undefined
																: () => {
																		submitCsatFeedback(
																			mid,
																		);
																	}
														}
													>
														{" "}
														<span>😐</span>
													</div>
													<div
														className={`gigauser-feedback-emoji ${csatFeedback === max ? "gigauser-active-emoji" : csatFeedback ? "gigauser-inactive-emoji" : "gigauser-regular-emoji"}`}
														onClick={
															csatFeedback
																? undefined
																: () => {
																		submitCsatFeedback(
																			max,
																		);
																	}
														}
													>
														{" "}
														<span>😃</span>
													</div>
												</div>
											</div>
											{csatFeedback ? (
												<div className="gigauser-feedback-2">
													<div>
														{
															feedbackEnterFeedbackTitle
														}
													</div>

													<textarea
														className="gigauser-feedback-textarea"
														value={messageFeedback}
														onChange={
															onChangeMessageFeedback
														}
														placeholder={
															feedbackEnterFeedbackPlaceholder
														}
													></textarea>

													<div
														className="gigauser-feedback-submit"
														onClick={
															submitMessageFeedback
														}
													>
														{feedbackSubmitText}
													</div>
												</div>
											) : null}
										</>
									)}
								</div>
							</div>
						) : null}
					</div>
				) : (
					<div className="gigauser-guide-container gigauser-loading-guide style-scroll">
						<div className="gigauser-loading-p">
							{" "}
							This is a random step 1{" "}
						</div>
						<div className="gigauser-loading-img">
							{" "}
							This is a random loading img
						</div>
						<div className="gigauser-loading-p">
							{" "}
							This is a random step 1{" "}
						</div>
						<div className="gigauser-loading-img">
							{" "}
							This is a random loading img
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
